import React, { useEffect, useRef } from 'react'
import styled, { keyframes } from 'styled-components'

interface ModalProps {
  /** Titolo visibile, usato anche come nome accessibile del dialog (WCAG 4.1.2). */
  title: string
  onClose: () => void
  children: React.ReactNode
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

export default function Modal({ title, onClose, children }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const titleId = useRef(`dialog-title-${Math.random().toString(36).slice(2, 9)}`)

  // All'apertura il focus entra nel dialog; alla chiusura torna al trigger (WCAG 2.4.3).
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    closeRef.current?.focus()
    return () => previouslyFocused?.focus()
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation()
      onClose()
      return
    }

    if (e.key !== 'Tab') return

    // Focus trap: finché il dialog è aperto il Tab non esce (WCAG 2.4.3).
    const focusables = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []
    )
    if (focusables.length === 0) return

    const first = focusables[0]
    const last = focusables[focusables.length - 1]

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  return (
    <Backdrop onClick={onClose}>
      <Box
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId.current}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <CloseButton ref={closeRef} type="button" onClick={onClose} aria-label="Chiudi">
          <span aria-hidden="true">&times;</span>
        </CloseButton>
        <Title id={titleId.current}>{title}</Title>
        <Content>{children}</Content>
      </Box>
    </Backdrop>
  )
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const Backdrop = styled.div`
  background-color: rgba(0, 0, 0, 0.7);
  position: fixed;
  inset: 0;
  display: grid;
  place-content: center;
  z-index: 9999999;
  animation: ${fadeIn} 0.3s linear;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

/* Niente height fissa: il testo deve poter crescere con il text spacing (WCAG 1.4.12). */
const Box = styled.div`
  position: relative;
  width: min(480px, calc(100vw - 2rem));
  min-height: 250px;
  padding: 4.5rem 3rem 3rem;
  font-size: 15px;
  background-color: #fcbb00;
  border-radius: 10px;
  text-align: center;
`

/* 44x44 supera il minimo di 24x24 richiesto da WCAG 2.5.8. */
const CloseButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 44px;
  height: 44px;
  font-size: 34px;
  line-height: 1;
  /* #222 su #fcbb00 = 9.4:1 — il bianco originale era 1.9:1 (WCAG 1.4.11). */
  color: #222222;
  background: transparent;
  border: 0;
  cursor: pointer;

  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 3px solid #005fcc;
    outline-offset: 2px;
    border-radius: 4px;
  }
`

/* #222 su #fcbb00 = 9.4:1 — il bianco originale era 1.9:1 (WCAG 1.4.3). */
const Title = styled.h2`
  margin: 0 0 1.5rem;
  font-size: 36px;
  font-weight: 700;
  color: #222222;
`

const Content = styled.div`
  font-size: 19px;
  font-weight: 700;
  color: #333333;
  line-height: 1.5;
`
